class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* Subscribe & Save — swap one-time item to subscription */
class CartSubscribeButton extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('.cart-item__subscribe-btn');
    if (this.button) {
      this.button.addEventListener('click', this.onClickHandler.bind(this));
    }
    this.abortController = null;
  }

  connectedCallback() {
    this.initSellingPlan();
  }

  async initSellingPlan() {
    const handle = this.dataset.productHandle;
    const variantId = this.dataset.variantId;
    if (!handle || !variantId) return;

    try {
      const product = await CartSubscribeButton.fetchProduct(handle);
      const planData = CartSubscribeButton.getSubscriptionData(product, parseInt(variantId));
      if (!planData) return;

      this.sellingPlanId = planData.planId;
      this.savings = planData.savings;

      const ctaText = (this.dataset.ctaText || 'Subscribe & save {{ amount }}')
        .replace('{{ amount }}', CartSubscribeButton.formatMoney(planData.savings));
      const freeShippingText = this.dataset.freeShippingText || '';

      const btnText = this.querySelector('.cart-item__subscribe-btn-text');
      if (btnText) {
        btnText.textContent = ctaText + (freeShippingText ? ' ' + freeShippingText : '');
      }

      this.button.disabled = false;
      this.removeAttribute('hidden');
    } catch (e) {
      console.error('CartSubscribeButton: failed to load selling plan', e);
    }
  }

  async onClickHandler(event) {
    event.preventDefault();
    if (this.classList.contains('loading') || !this.sellingPlanId) return;

    this.classList.add('loading');
    this.button.disabled = true;
    this.abortController = new AbortController();

    const itemKey = this.dataset.itemKey;
    const variantId = this.dataset.variantId;
    const quantity = parseInt(this.dataset.quantity) || 1;
    const cartDrawer = document.querySelector('cart-drawer');

    try {
      // Step 1: Remove the one-time item
      const removeResponse = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({ id: itemKey, quantity: 0 }),
        signal: this.abortController.signal,
      });
      if (!removeResponse.ok) throw new Error('Failed to remove item');

      // Step 2: Re-add with selling plan
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', quantity);
      formData.append('selling_plan', this.sellingPlanId);
      if (cartDrawer) {
        formData.append('sections', cartDrawer.getSectionsToRender().map((s) => s.id));
        formData.append('sections_url', window.location.pathname);
      }

      const addConfig = fetchConfig('javascript');
      addConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete addConfig.headers['Content-Type'];
      addConfig.body = formData;
      addConfig.signal = this.abortController.signal;

      const addResponse = await fetch(routes.cart_add_url, addConfig);
      const addData = await addResponse.json();

      if (addData.status) {
        throw new Error(addData.description || 'Failed to add subscription item');
      }

      if (cartDrawer) {
        cartDrawer.renderContents(addData);
      }
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'subscribe-swap', cartData: addData });
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Subscribe swap failed:', error);

      // Rollback: re-add item without selling plan
      try {
        const rollbackData = new FormData();
        rollbackData.append('id', variantId);
        rollbackData.append('quantity', quantity);
        if (cartDrawer) {
          rollbackData.append('sections', cartDrawer.getSectionsToRender().map((s) => s.id));
          rollbackData.append('sections_url', window.location.pathname);
        }
        const rollbackConfig = fetchConfig('javascript');
        rollbackConfig.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete rollbackConfig.headers['Content-Type'];
        rollbackConfig.body = rollbackData;

        const rollbackResponse = await fetch(routes.cart_add_url, rollbackConfig);
        const rollbackResult = await rollbackResponse.json();
        if (cartDrawer && !rollbackResult.status) {
          cartDrawer.renderContents(rollbackResult);
        }
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'subscribe-swap-rollback', cartData: rollbackResult });
      } catch (rollbackError) {
        console.error('Subscribe swap rollback also failed:', rollbackError);
      }
    } finally {
      this.classList.remove('loading');
      this.abortController = null;
    }
  }

  static _productCache = new Map();

  static async fetchProduct(handle) {
    if (CartSubscribeButton._productCache.has(handle)) {
      return CartSubscribeButton._productCache.get(handle);
    }
    const response = await fetch(`/products/${handle}.js`);
    if (!response.ok) throw new Error(`Product fetch failed: ${handle}`);
    const product = await response.json();
    CartSubscribeButton._productCache.set(handle, product);
    return product;
  }

  static getSubscriptionData(product, variantId) {
    const variant = product.variants.find((v) => v.id === variantId);
    if (!variant || !product.selling_plan_groups || product.selling_plan_groups.length === 0) {
      return null;
    }

    for (const group of product.selling_plan_groups) {
      for (const plan of group.selling_plans) {
        if (!plan.price_adjustments || plan.price_adjustments.length === 0) continue;
        const adj = plan.price_adjustments[0];
        let subPrice;

        if (adj.value_type === 'fixed_amount') {
          subPrice = variant.price - adj.value;
        } else if (adj.value_type === 'percentage') {
          subPrice = Math.round((variant.price * (100 - adj.value)) / 100);
        } else if (adj.value_type === 'price') {
          subPrice = adj.value;
        }

        if (subPrice != null && subPrice >= 0) {
          return {
            planId: plan.id,
            subPrice: subPrice,
            savings: variant.price - subPrice,
            planName: plan.name,
          };
        }
      }
    }
    return null;
  }

  static formatMoney(cents) {
    const amount = (cents / 100).toFixed(0);
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      return amount + ' ' + window.Shopify.currency.active;
    }
    return amount + ' kr.';
  }
}

customElements.define('cart-subscribe-button', CartSubscribeButton);

/* Recommended add-on quick add */
class CartAddonButton extends HTMLElement {
  constructor() {
    super();
    const btn = this.querySelector('button');
    if (btn) {
      btn.addEventListener('click', this.onClickHandler.bind(this));
    }
  }

  async onClickHandler(event) {
    event.preventDefault();
    if (this.classList.contains('loading')) return;

    this.classList.add('loading');
    const variantId = this.dataset.variantId;
    const cartDrawer = document.querySelector('cart-drawer');

    try {
      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      if (cartDrawer) {
        formData.append('sections', cartDrawer.getSectionsToRender().map((s) => s.id));
        formData.append('sections_url', window.location.pathname);
      }

      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];
      config.body = formData;

      const response = await fetch(routes.cart_add_url, config);
      const data = await response.json();

      if (data.status) {
        throw new Error(data.description || 'Failed to add item');
      }

      if (cartDrawer) {
        cartDrawer.renderContents(data);
      }
      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'addon-add', cartData: data });
    } catch (error) {
      console.error('Add-on failed:', error);
    } finally {
      this.classList.remove('loading');
    }
  }
}

customElements.define('cart-addon-button', CartAddonButton);
